import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Search, Eye, Edit2, Trash2, Paperclip, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useAnnounce } from '../../hooks/useAnnounce';
import { tripCreditExGST } from '../../utils/reportHelpers';

// Returns the GST-exclusive total cost for a trip (sector costs back-calculated + fees already
// ex-GST), less any travel credit applied — this is what the client is actually charged, and
// treating it as gross would overstate their spend. See tripCreditExGST in reportHelpers.
export function calcTripExGST(trip, gstRate = 0.1) {
  const sectors = (trip.sectors || []).reduce((sum, s) => {
    const c = parseFloat(s.cost) || 0;
    return sum + (s.international ? c : c / (1 + gstRate));
  }, 0);
  const fees = (trip.fees || []).reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);
  return Math.max(0, sectors + fees - tripCreditExGST(trip));
}

export const STATUS_CONFIG = {
  draft:            { label: 'Draft',           cls: 'bg-gray-100 text-gray-600' },
  pending_approval: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700' },
  approved:         { label: 'Approved',         cls: 'bg-green-100 text-green-700' },
  declined:         { label: 'Declined',         cls: 'bg-red-100 text-red-700' },
  booked:           { label: 'Booked',           cls: 'bg-indigo-100 text-indigo-700' },
  travelling:       { label: 'Travelling',       cls: 'bg-purple-100 text-purple-700' },
  completed:        { label: 'Completed',        cls: 'bg-teal-100 text-teal-700' },
  cancelled:        { label: 'Cancelled',        cls: 'bg-gray-200 text-gray-700' },
};

export function getDisplayStatus(trip) {
  if (trip.status !== 'booked') return trip.status;
  const today = new Date().toISOString().slice(0, 10);
  if (trip.endDate && today > trip.endDate)      return 'completed';
  if (trip.startDate && today >= trip.startDate) return 'travelling';
  return 'booked';
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  return `${d}-${MONTHS[parseInt(m, 10) - 1]}-${y.slice(2)}`;
}

export function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export function leadTimeDays(trip) {
  if (!trip.startDate || !trip.createdAt) return null;
  const created = trip.createdAt?.toDate ? trip.createdAt.toDate() : new Date(trip.createdAt);
  const start = new Date(trip.startDate + 'T00:00:00');
  const days = Math.round((start - created) / 86400000);
  return days < 0 ? 0 : days;
}

export function LeadTimeBadge({ days, showDays = false }) {
  if (days === null || days === undefined) return <span className="text-gray-500">—</span>;
  let label, cls;
  if (days <= 3)       { label = '0–3 days';   cls = 'bg-red-100 text-red-700'; }
  else if (days <= 10) { label = '4–10 days';  cls = 'bg-amber-100 text-amber-700'; }
  else if (days <= 20) { label = '11–20 days'; cls = 'bg-yellow-100 text-yellow-700'; }
  else                 { label = '21+ days';   cls = 'bg-green-100 text-green-700'; }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
      title={`${days} day${days !== 1 ? 's' : ''} between booking and travel`}
    >
      {showDays ? `${days}d` : label}
    </span>
  );
}

// Returns days a trip has been waiting for approval (null if not pending)
export function awaitingDays(trip) {
  if (trip.status !== 'pending_approval') return null;
  const submissions = (trip.amendments || []).filter(a => a.type === 'status_change' && a.to === 'pending_approval');
  if (submissions.length > 0) {
    const latest = submissions.reduce((a, b) => a.at > b.at ? a : b);
    return Math.max(0, Math.round((Date.now() - new Date(latest.at)) / 86400000));
  }
  if (trip.updatedAt) {
    const d = trip.updatedAt?.toDate ? trip.updatedAt.toDate() : new Date(trip.updatedAt);
    return Math.max(0, Math.round((Date.now() - d) / 86400000));
  }
  return null;
}

// ── Passengers cell ───────────────────────────────────────────────────────────

function PassengersCell({ trip }) {
  const [open, setOpen] = useState(false);
  const extra = trip.additionalPassengers?.length || 0;

  return (
    <div className="relative">
      <span className="text-gray-600">{trip.travellerName || '—'}</span>
      {extra > 0 && (
        <>
          {' '}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
          >
            +{extra} more
          </button>
          {open && (
            <div
              className="absolute z-20 left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg p-3 space-y-1"
              onClick={e => e.stopPropagation()}
            >
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">All passengers</p>
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" aria-hidden="true" />
                {trip.travellerName}
                <span className="text-xs text-gray-600 ml-auto">Primary</span>
              </div>
              {trip.additionalPassengers.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0" aria-hidden="true" />
                  {p.name}
                  {p.costCentre && <span className="text-xs text-gray-600 ml-auto truncate">{p.costCentre}</span>}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-2 w-full text-xs text-gray-600 hover:text-gray-800 flex items-center justify-center gap-1"
              >
                <X size={11} aria-hidden="true" /> Close
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Date range helpers ────────────────────────────────────────────────────────

function pad(n) { return String(n).padStart(2, '0'); }
function isoDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

function getQuickRange(key) {
  const now  = new Date();
  const y    = now.getFullYear();
  const m    = now.getMonth(); // 0-based
  if (key === 'thisMonth') {
    return { from: `${y}-${pad(m + 1)}-01`, to: isoDate(new Date(y, m + 1, 0)) };
  }
  if (key === 'lastMonth') {
    const last = new Date(y, m, 0);
    return { from: `${last.getFullYear()}-${pad(last.getMonth() + 1)}-01`, to: isoDate(last) };
  }
  if (key === 'thisQuarter') {
    const q = Math.floor(m / 3);
    return { from: isoDate(new Date(y, q * 3, 1)), to: isoDate(new Date(y, q * 3 + 3, 0)) };
  }
  if (key === 'lastQuarter') {
    const q = Math.floor(m / 3);
    const s = q === 0 ? new Date(y - 1, 9, 1) : new Date(y, (q - 1) * 3, 1);
    const e = q === 0 ? new Date(y - 1, 12, 0) : new Date(y, q * 3, 0);
    return { from: isoDate(s), to: isoDate(e) };
  }
  if (key === 'thisFY') {
    const fyStart = m >= 6 ? y : y - 1;
    return { from: `${fyStart}-07-01`, to: `${fyStart + 1}-06-30` };
  }
  if (key === 'lastFY') {
    const fyStart = (m >= 6 ? y : y - 1) - 1;
    return { from: `${fyStart}-07-01`, to: `${fyStart + 1}-06-30` };
  }
  return { from: '', to: '' };
}

const QUICK_OPTIONS = [
  { key: 'thisMonth',    label: 'This month' },
  { key: 'lastMonth',    label: 'Last month' },
  { key: 'thisQuarter',  label: 'This quarter' },
  { key: 'lastQuarter',  label: 'Last quarter' },
  { key: 'thisFY',       label: 'This FY' },
  { key: 'lastFY',       label: 'Last FY' },
];

const sel = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500 bg-white';

export default function TripList({ trips, loading, onNew, onView, onEdit, onDelete, canCreate, initialStatusFilter = '' }) {
  const { userProfile } = useAuth();
  const { isSTX } = useTenant();
  const announce = useAnnounce();

  const [search,          setSearch]          = useState('');
  const [statusFilter,    setStatusFilter]    = useState(initialStatusFilter);
  const [tripTypeFilter,  setTripTypeFilter]  = useState('');
  const [costCentreFilter,setCostCentreFilter]= useState('');
  const [destCityFilter,  setDestCityFilter]  = useState('');
  const [dateFrom,        setDateFrom]        = useState('');
  const [dateTo,          setDateTo]          = useState('');
  const [quickDate,       setQuickDate]       = useState('');

  const isAdmin    = userProfile?.role === 'stx_admin';
  const canEditTrip= ['stx_admin', 'stx_ops', 'client_ops', 'client_traveller'].includes(userProfile?.role);

  // Derive unique values for filter dropdowns from the full (unfiltered) trips list
  const allTripTypes   = useMemo(() => [...new Set(trips.map(t => t.tripType).filter(Boolean))].sort(), [trips]);
  const allCostCentres = useMemo(() => [...new Set(trips.map(t => t.costCentre).filter(Boolean))].sort(), [trips]);
  const allDestCities  = useMemo(() => [...new Set(trips.map(t => t.destinationCity).filter(Boolean))].sort(), [trips]);

  const applyQuick = (key) => {
    const { from, to } = getQuickRange(key);
    setDateFrom(from);
    setDateTo(to);
    setQuickDate(key);
  };

  const clearDateFilters = () => { setDateFrom(''); setDateTo(''); setQuickDate(''); };

  const hasFilters = !!(statusFilter || tripTypeFilter || costCentreFilter || destCityFilter || dateFrom || dateTo);
  const clearAll   = () => {
    setStatusFilter(''); setTripTypeFilter(''); setCostCentreFilter('');
    setDestCityFilter(''); clearDateFilters();
  };

  const filtered = useMemo(() => {
    return trips.filter(t => {
      const q = search.toLowerCase();
      if (q && !t.title?.toLowerCase().includes(q) && !t.travellerName?.toLowerCase().includes(q) &&
          !t.destinationCity?.toLowerCase().includes(q) && !t.originCity?.toLowerCase().includes(q) &&
          !(t.additionalPassengers || []).some(p => p.name?.toLowerCase().includes(q))) return false;
      if (statusFilter    && getDisplayStatus(t) !== statusFilter)  return false;
      if (tripTypeFilter  && t.tripType   !== tripTypeFilter)        return false;
      if (costCentreFilter&& t.costCentre !== costCentreFilter)      return false;
      if (destCityFilter  && t.destinationCity !== destCityFilter)   return false;
      if (dateFrom        && (t.startDate || '') < dateFrom)         return false;
      if (dateTo          && (t.startDate || '') > dateTo)           return false;
      return true;
    });
  }, [trips, search, statusFilter, tripTypeFilter, costCentreFilter, destCityFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (hasFilters) {
      announce(`${filtered.length} trip${filtered.length !== 1 ? 's' : ''} found`);
    }
  }, [filtered.length, hasFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasPendingApproval = filtered.some(t => t.status === 'pending_approval');

  if (loading) {
    return <p className="text-sm text-gray-600 py-8 text-center">Loading trips…</p>;
  }

  return (
    <div>
      {/* ── Row 1: Search + New trip ── */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={15} aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="search"
            aria-label="Search trips"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search trips…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
          />
        </div>
        {canCreate && (
          <button
            onClick={onNew}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 ml-auto"
          >
            <Plus size={15} /> New trip
          </button>
        )}
      </div>

      {/* ── Row 2: Dropdown filters ── */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={statusFilter}     onChange={e => setStatusFilter(e.target.value)}     className={sel}
          aria-label="Filter by status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        {allTripTypes.length > 0 && (
          <select value={tripTypeFilter} onChange={e => setTripTypeFilter(e.target.value)} className={sel}
            aria-label="Filter by trip type">
            <option value="">All trip types</option>
            {allTripTypes.map(t => <option key={t}>{t}</option>)}
          </select>
        )}

        {allCostCentres.length > 0 && (
          <select value={costCentreFilter} onChange={e => setCostCentreFilter(e.target.value)} className={sel}
            aria-label="Filter by cost centre">
            <option value="">All cost centres</option>
            {allCostCentres.map(c => <option key={c}>{c}</option>)}
          </select>
        )}

        {allDestCities.length > 0 && (
          <select value={destCityFilter} onChange={e => setDestCityFilter(e.target.value)} className={sel}
            aria-label="Filter by destination">
            <option value="">All destinations</option>
            {allDestCities.map(c => <option key={c}>{c}</option>)}
          </select>
        )}

        {hasFilters && (
          <button
            onClick={clearAll}
            className="flex items-center gap-1 text-xs text-gray-700 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100 ml-auto"
          >
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      {/* ── Row 3: Date filter ── */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <span className="text-xs font-medium text-gray-700 shrink-0">Travel dates:</span>
        <div className="flex flex-wrap gap-1">
          {QUICK_OPTIONS.map(o => (
            <button
              key={o.key}
              onClick={() => quickDate === o.key ? clearDateFilters() : applyQuick(o.key)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                quickDate === o.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <input
            type="date"
            aria-label="Filter trips starting on or after"
            value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setQuickDate(''); }}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
          />
          <span className="text-gray-600 text-xs" aria-hidden="true">→</span>
          <input
            type="date"
            aria-label="Filter trips starting on or before"
            value={dateTo}
            onChange={e => { setDateTo(e.target.value); setQuickDate(''); }}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
          />
          {(dateFrom || dateTo) && (
            <button onClick={clearDateFilters} aria-label="Clear date filter" className="text-gray-600 hover:text-gray-900 p-0.5 rounded">
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* ── Trip list ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-600 text-sm">
            {trips.length === 0
              ? 'No trips yet. Click "New trip" to create one.'
              : 'No trips match your filters.'}
          </div>
        ) : (
          <>
            {/* Mobile card list — visible below md */}
            <ul className="md:hidden divide-y divide-gray-100 list-none m-0 p-0">
              {filtered.map(trip => (
                <li
                  key={trip.id}
                  onClick={() => onView(trip)}
                  className="px-4 py-3 cursor-pointer hover:bg-blue-50 active:bg-blue-100 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                        {trip.title || '—'}
                        {(trip.attachments?.length ?? 0) > 0 && (
                          <Paperclip size={11} aria-hidden="true" className="text-gray-500 shrink-0" />
                        )}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5 truncate">
                        {trip.travellerName || '—'}
                        {trip.tripType ? ` · ${trip.tripType}` : ''}
                        {isSTX && trip.clientId ? ` · ${trip.clientId}` : ''}
                      </p>
                      {(trip.startDate || trip.destinationCity) && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {trip.startDate ? fmtDate(trip.startDate) : ''}
                          {trip.endDate && trip.endDate !== trip.startDate ? ` → ${fmtDate(trip.endDate)}` : ''}
                          {trip.destinationCity ? ` · ${trip.destinationCity}` : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <StatusBadge status={getDisplayStatus(trip)} />
                      {(() => { const v = calcTripExGST(trip); return v > 0 ? <span className="text-xs text-gray-700 font-medium tabular-nums">A${v.toFixed(2)}</span> : null; })()}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-2" onClick={e => e.stopPropagation()}>
                    <button onClick={() => onView(trip)} aria-label={`View trip: ${trip.title}`} className="text-blue-600 hover:text-blue-800 p-1.5 rounded">
                      <Eye size={14} aria-hidden="true" />
                    </button>
                    {canEditTrip && trip.status !== 'cancelled' && (
                      <button onClick={() => onEdit(trip)} aria-label={`Edit trip: ${trip.title}`} className="text-gray-600 hover:text-gray-900 p-1.5 rounded">
                        <Edit2 size={14} aria-hidden="true" />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => onDelete(trip)} aria-label={`Delete trip: ${trip.title}`} className="text-gray-600 hover:text-red-600 p-1.5 rounded">
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop table — visible from md up */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600 hidden sm:table-cell">Ref</th>
                    <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600">Trip</th>
                    <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600">Traveller</th>
                    {isSTX && <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600">Client</th>}
                    <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                    <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Destination</th>
                    <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600">Dates</th>
                    <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell" title="Days between booking and travel date">Lead Time</th>
                    {hasPendingApproval && <th scope="col" className="text-right px-4 py-3 font-medium text-gray-600 hidden xl:table-cell" title="Days waiting for approval">Awaiting</th>}
                    <th scope="col" className="text-right px-4 py-3 font-medium text-gray-600">Ex-GST</th>
                    <th scope="col" className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((trip, i) => (
                    <tr
                      key={trip.id}
                      className={`border-b border-gray-100 last:border-0 hover:bg-blue-50 cursor-pointer transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      onClick={() => onView(trip)}
                    >
                      <td className="px-4 py-3 text-xs text-gray-600 font-mono hidden sm:table-cell whitespace-nowrap">
                        {trip.tripRef || '—'}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        <span className="flex items-center gap-1.5">
                          {trip.title || '—'}
                          {(trip.attachments?.length ?? 0) > 0 && (
                            <Paperclip size={11} aria-hidden="true" className="text-gray-500 shrink-0" />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <PassengersCell trip={trip} />
                      </td>
                      {isSTX && <td className="px-4 py-3 text-gray-700 text-xs">{trip.clientId || '—'}</td>}
                      <td className="px-4 py-3 text-gray-700">{trip.tripType || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 hidden md:table-cell">
                        {trip.originCity && trip.destinationCity
                          ? <span className="text-xs">{trip.originCity} → {trip.destinationCity}</span>
                          : trip.destinationCity
                            ? <span className="text-xs">{trip.destinationCity}</span>
                            : <span className="text-gray-500">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap text-xs">
                        {trip.startDate
                          ? `${fmtDate(trip.startDate)}${trip.endDate && trip.endDate !== trip.startDate ? ` → ${fmtDate(trip.endDate)}` : ''}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <LeadTimeBadge days={leadTimeDays(trip)} />
                      </td>
                      {hasPendingApproval && (
                        <td className="px-4 py-3 text-right hidden xl:table-cell">
                          {(() => {
                            const d = awaitingDays(trip);
                            if (d === null) return <span className="text-gray-500">—</span>;
                            return (
                              <span className={`text-xs font-medium ${d >= 7 ? 'text-red-600' : d >= 3 ? 'text-amber-600' : 'text-gray-700'}`}>
                                {d}d
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right text-gray-600 text-xs tabular-nums whitespace-nowrap">
                        {(() => { const v = calcTripExGST(trip); return v > 0 ? `A$${v.toFixed(2)}` : '—'; })()}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={getDisplayStatus(trip)} /></td>
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => onView(trip)} aria-label={`View trip: ${trip.title}`} className="text-blue-600 hover:text-blue-800 p-1 rounded">
                            <Eye size={14} aria-hidden="true" />
                          </button>
                          {canEditTrip && trip.status !== 'cancelled' && (
                            <button onClick={() => onEdit(trip)} aria-label={`Edit trip: ${trip.title}`} className="text-gray-600 hover:text-gray-900 p-1 rounded">
                              <Edit2 size={14} aria-hidden="true" />
                            </button>
                          )}
                          {isAdmin && (
                            <button onClick={() => onDelete(trip)} aria-label={`Delete trip: ${trip.title}`} className="text-gray-600 hover:text-red-600 p-1 rounded">
                              <Trash2 size={14} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <p className="text-xs text-gray-600 mt-2" aria-live="polite">
        {filtered.length} of {trips.length} trip{trips.length !== 1 ? 's' : ''}
        {hasFilters && ' · filters active'}
      </p>
    </div>
  );
}
